import { Box, LinearProgress, Stack, Typography } from "@mui/material"
import STRING_CONSTANTS from "../../shared/constants/StringConstants"
import { SCHEME_STATUS } from "./constants"
import { formatAmount, formatDate } from "./format"
import SchemeStatusChip from "./SchemeStatusChip"
import schemeTokens, { softPrimary } from "./tokens"

const Stat = ({ label, value, highlight }) => (
  <Box>
    <Typography sx={schemeTokens.text.statLabel}>{label}</Typography>
    <Typography
      sx={{
        ...schemeTokens.text.statValue,
        color: highlight ? "primary.main" : "text.primary",
      }}
    >
      {value}
    </Typography>
  </Box>
)

const getClosedLabel = (scheme) => {
  if (scheme.status === SCHEME_STATUS.CANCELLED) {
    return {
      label: STRING_CONSTANTS.CANCELLED_ON_LABEL,
      value: scheme.cancelledAt,
    }
  }
  if (scheme.status === SCHEME_STATUS.MATURED) {
    return { label: STRING_CONSTANTS.MATURED_ON_LABEL, value: scheme.maturedAt }
  }
  return null
}

const SchemeSummaryCard = ({ scheme, metrics }) => {
  const closedLabel = getClosedLabel(scheme)

  return (
  <Box
    sx={{
      borderRadius: schemeTokens.radius.card,
      bgcolor: softPrimary(0.08),
      px: 2,
      py: 2,
    }}
  >
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={1.5}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={schemeTokens.text.cardTitle} noWrap>
            {scheme.schemeMaster?.name || STRING_CONSTANTS.SCHEME}
          </Typography>
          <SchemeStatusChip status={scheme.status} />
        </Stack>
        <Typography sx={{ ...schemeTokens.text.hint, mt: 0.25 }}>
          {scheme.id}
        </Typography>
      </Box>
      <Typography sx={{ ...schemeTokens.text.amount, flexShrink: 0 }}>
        {formatAmount(metrics.monthlyAmount)}
      </Typography>
    </Stack>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1.5,
        mt: 2,
      }}
    >
      <Stat
        label={STRING_CONSTANTS.START_DATE}
        value={formatDate(scheme.enrolledAt)}
      />
      <Stat
        label={closedLabel ? closedLabel.label : STRING_CONSTANTS.MATURITY_DATE}
        value={formatDate(
          closedLabel ? closedLabel.value : metrics.maturityDate
        )}
      />
      <Stat
        label={STRING_CONSTANTS.PAID_INSTALMENTS}
        value={`${metrics.paidCount} of ${metrics.totalInstalments}`}
      />
      <Stat
        label={STRING_CONSTANTS.TOTAL_PAID}
        value={formatAmount(metrics.paidTotal)}
        highlight
      />
    </Box>

    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mt: 2 }}
    >
      <Typography sx={schemeTokens.text.hint}>
        {STRING_CONSTANTS.CUSTOMER_CONTRIBUTION}
      </Typography>
      <Typography
        sx={{ fontSize: 12, fontWeight: 700, color: "primary.main" }}
      >
        {metrics.progressPercent}%
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={metrics.progressPercent}
      sx={{
        mt: 0.75,
        height: 6,
        borderRadius: schemeTokens.radius.pill,
        bgcolor: softPrimary(0.18),
      }}
    />

    {metrics.isInGrace && metrics.graceEndsOn && (
      <Typography
        sx={{ ...schemeTokens.text.hint, mt: 1.25, color: "warning.dark" }}
      >
        {`${STRING_CONSTANTS.GRACE_PERIOD_ACTIVE} ${formatDate(
          metrics.graceEndsOn
        )}`}
      </Typography>
    )}

    {metrics.missWarning && (
      <Typography
        sx={{ ...schemeTokens.text.hint, mt: 0.75, color: "error.main" }}
      >
        {metrics.missWarning}
      </Typography>
    )}

    {!metrics.contributionApplies && (
      <Typography
        sx={{ ...schemeTokens.text.hint, mt: 0.75, color: "text.secondary" }}
      >
        {STRING_CONSTANTS.CONTRIBUTION_NOT_APPLICABLE}
      </Typography>
    )}
  </Box>
  )
}

export default SchemeSummaryCard
